<template>
  <div>
    <InventoryPageHeader>
      <template #title>
        <i class="fas fa-upload mr-2 text-primary"></i>Batch Import
      </template>
    </InventoryPageHeader>

    <b-container fluid class="mt--6">

      <!-- Mode Toggle -->
      <b-card no-body class="card-shadow mb-3">
        <b-card-header><i class="fas fa-sliders-h mr-2"></i>Import Mode</b-card-header>
        <b-card-body>
          <b-form-radio-group v-model="importMode" buttons button-variant="outline-primary" size="sm">
            <b-form-radio value="tracking">
              <i class="fas fa-file-excel mr-1"></i> From Tracking Sheet (Excel with all columns)
            </b-form-radio>
            <b-form-radio value="bulk">
              <i class="fas fa-list mr-1"></i> Bulk MAC Import (CSV / Excel — MAC addresses only)
            </b-form-radio>
          </b-form-radio-group>
          <p class="text-muted mt-2 mb-0" style="font-size:0.85rem;">
            <template v-if="importMode === 'tracking'">
              Upload your <strong>Device tracking</strong> Excel file. Batch, location, status and notes are read from each row automatically.
            </template>
            <template v-else>
              Upload any file with MAC addresses. You'll assign batch, location and status manually for all devices.
            </template>
          </p>
        </b-card-body>
      </b-card>

      <!-- ═══════════════════════════════════════════════════
           MODE A: Tracking Sheet Import
      ════════════════════════════════════════════════════ -->
      <template v-if="importMode === 'tracking'">
        <b-card no-body class="card-shadow mb-3">
          <b-card-header><i class="fas fa-file-upload mr-2"></i>Upload Tracking Sheet</b-card-header>
          <b-card-body>
            <b-form-group label="Select your Device_tracking Excel file (.xlsx)">
              <b-form-file
                v-model="trackingFile"
                accept=".xlsx,.xlsm"
                placeholder="Choose file or drag here..."
                size="sm"
                @change="onTrackingFileChange"
              />
            </b-form-group>

            <!-- Preview -->
            <div v-if="trackingPreview.length > 0" class="mt-3">
              <h6 class="text-muted">Preview — first {{ trackingPreview.length }} rows</h6>
              <div class="table-responsive" style="max-height:280px; overflow-y:auto;">
                <table class="table table-sm table-bordered mb-0" style="font-size:0.8rem;">
                  <thead class="thead-light">
                    <tr>
                      <th>MAC</th><th>Batch</th><th>Owner</th><th>Location</th><th>Status</th><th>Quality Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in trackingPreview" :key="i">
                      <td class="font-weight-bold">{{ r.mac }}</td>
                      <td>{{ r.batch }}</td>
                      <td>{{ r.owner }}</td>
                      <td>{{ r.location }}</td>
                      <td><b-badge :variant="r.status === 'OK' ? 'success' : 'secondary'">{{ r.status }}</b-badge></td>
                      <td><span v-if="r.quality" class="text-danger">{{ r.quality }}</span><span v-else class="text-muted">—</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <small class="text-muted">Showing first {{ trackingPreview.length }} of {{ trackingTotalRows }} rows detected.</small>
            </div>
          </b-card-body>
        </b-card>

        <div class="text-center mb-3">
          <base-button
            type="primary"
            :disabled="!trackingFile || trackingImporting"
            @click="importTrackingSheet"
          >
            <i v-if="trackingImporting" class="fas fa-spinner fa-pulse mr-1"></i>
            {{ trackingImporting ? 'Importing...' : `Import ${trackingTotalRows} Device(s) from Sheet` }}
          </base-button>
          <base-button type="outline-secondary" class="ml-2" @click="clearTracking">
            <i class="fas fa-times"></i> Clear
          </base-button>
        </div>

        <!-- Result -->
        <b-alert v-if="trackingResult" :variant="trackingResult.success ? 'success' : 'danger'" show class="mb-3">
          <template v-if="trackingResult.success">
            <strong>Import complete!</strong><br/>
            {{ trackingResult.created_devices }} created,
            {{ trackingResult.updated_devices }} updated,
            {{ trackingResult.created_companies }} new companies,
            {{ trackingResult.created_batches }} new batches.
            <div v-if="trackingResult.errors && trackingResult.errors.length" class="mt-1">
              <small>Errors: {{ trackingResult.errors.join(', ') }}</small>
            </div>
          </template>
          <template v-else>
            <strong>Import failed:</strong> {{ trackingResult.error }}
          </template>
        </b-alert>
      </template>

      <!-- ═══════════════════════════════════════════════════
           MODE B: Bulk MAC Import (existing logic)
      ════════════════════════════════════════════════════ -->
      <template v-else>
        <b-card no-body class="card-shadow mb-3">
          <b-card-header><i class="fas fa-file-upload mr-2"></i>Upload File</b-card-header>
          <b-card-body>
            <b-form-group label="Select a CSV or Excel file containing MAC addresses">
              <b-form-file
                v-model="file"
                accept=".csv,.xlsx,.xls"
                placeholder="Choose a file or drag it here..."
                size="sm"
                @change="onFileChange"
              />
            </b-form-group>

            <div v-if="rows.length > 0" class="mt-3">
              <h6 class="text-muted">Parsed Devices: {{ rows.length }}</h6>
              <div class="table-responsive" style="max-height:300px; overflow-y:auto;">
                <table class="table table-sm table-bordered mb-0">
                  <thead class="thead-light">
                    <tr><th>MAC Address</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in rows" :key="idx">
                      <td>{{ row.mac }}</td>
                      <td>
                        <b-badge v-if="row.exists" variant="warning">Already exists</b-badge>
                        <b-badge v-else variant="success">Will be imported</b-badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-card-body>
        </b-card>

        <b-card no-body class="card-shadow mb-3">
          <b-card-header><i class="fas fa-tags mr-2"></i>Device Details</b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="4">
                <b-form-group label="Current Location">
                  <b-form-select v-model="bulkLocation" size="sm">
                    <option :value="null" disabled>Select location or create new...</option>
                    <option value="__new__">-- Create New Company --</option>
                    <option v-for="c in allCompanies" :key="c" :value="c">{{ c }}</option>
                  </b-form-select>
                  <b-input-group v-if="bulkLocation === '__new__'" size="sm" class="mt-1">
                    <b-form-input v-model="newCompanyName" placeholder="New company name..." />
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Status">
                  <b-form-select v-model="bulkStatus" :options="statusOptions" size="sm" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Batch (optional)">
                  <b-form-select v-model="bulkBatch" size="sm">
                    <option :value="null" disabled>Select batch or create new...</option>
                    <option value="__new__">-- Create New Batch --</option>
                    <option v-for="b in batchOptions" :key="b.value" :value="b.value">{{ b.text }}</option>
                  </b-form-select>
                  <b-row v-if="bulkBatch === '__new__'" class="mt-1">
                    <b-col cols="4">
                      <b-form-input v-model="newBatch.number" placeholder="Number" size="sm" />
                    </b-col>
                    <b-col cols="4">
                      <b-form-input v-model="newBatch.characteristics" placeholder="Chars" size="sm" />
                    </b-col>
                    <b-col cols="4">
                      <b-form-input v-model="newBatch.deliveryDate" type="date" size="sm" />
                    </b-col>
                  </b-row>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group label="Notes (applied to all imported devices)" class="mt-2">
              <b-form-textarea v-model="bulkNotes" rows="2" placeholder="e.g. Imported from Tunisia batch..." size="sm" />
            </b-form-group>
          </b-card-body>
        </b-card>

        <div class="text-center mb-3">
          <base-button type="primary" :disabled="rows.length === 0 || importing" @click="importDevices">
            <i v-if="importing" class="fas fa-spinner fa-pulse mr-1"></i>
            Import {{ newDevicesCount }} Device(s)
          </base-button>
          <base-button type="outline-secondary" @click="clearAll" class="ml-2">
            <i class="fas fa-times"></i> Clear
          </base-button>
        </div>
      </template>

    </b-container>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { statusOptionsList } from '@/utils/statusHelpers'
import * as XLSX from 'xlsx'
import InventoryPageHeader from '@/components/InventoryPageHeader.vue'

export default {
  name: 'ImportPage',
  components: { InventoryPageHeader },

  data() {
    return {
      importMode: 'tracking',   // 'tracking' | 'bulk'

      // ── Tracking sheet mode ──
      trackingFile: null,
      trackingPreview: [],
      trackingTotalRows: 0,
      trackingImporting: false,
      trackingResult: null,

      // ── Bulk MAC mode ──
      file: null,
      rows: [],
      importing: false,
      bulkLocation: null,
      bulkStatus: 'OK',
      bulkBatch: null,
      bulkNotes: '',
      newCompanyName: '',
      newBatch: { number: '', characteristics: '', deliveryDate: '' }
    }
  },

  computed: {
    ...mapState('inventory', ['devices', 'companies', 'batches']),
    allCompanies() { return this.companies },
    statusOptions() { return statusOptionsList.map(s => ({ value: s, text: s })) },
    batchOptions() {
      return this.batches.map(b => ({
        value: `Batch ${b.number} (${b.deliveryDate})`,
        text:  `Batch ${b.number} (${b.deliveryDate})`
      }))
    },
    newDevicesCount() { return this.rows.filter(r => !r.exists).length }
  },

  methods: {
    ...mapActions('inventory', ['addDevice', 'addCompany', 'addBatch', 'loadDevices', 'loadCompanies', 'loadBatches']),

    // ─────────────────────────────────────────
    // MODE A: Tracking sheet
    // ─────────────────────────────────────────
    onTrackingFileChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.trackingResult = null
      this.previewTrackingFile(file)
    },

    async previewTrackingFile(file) {
      try {
        const data = await file.arrayBuffer()
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets['Device tracking']
        if (!ws) { alert("Sheet 'Device tracking' not found in this file."); return }

        const json = XLSX.utils.sheet_to_json(ws, { header: 1 })

        // Find header row
        let headerIdx = json.findIndex(row => row.some(c => String(c).includes('MAC Address')))
        if (headerIdx === -1) { alert('Could not find header row.'); return }

        const headers = json[headerIdx].map(c => String(c || '').trim())
        const dataRows = json.slice(headerIdx + 1).filter(r => r[0]) // skip empty

        const col = name => headers.findIndex(h => h.toLowerCase().includes(name.toLowerCase()))
        const iMac  = col('MAC Address')
        const iBatch = col('Delivery batch')
        const iLoc  = col('Current location')
        const iStat = col('Current Status')
        const iQual = col('Quality issues')
        const iOwner = col('Owner')  

        const parsed = dataRows
          .map(r => ({
            mac:      String(r[iMac]  || '').trim(),
            batch:    String(r[iBatch] || '').trim(),
            owner:    String(r[iOwner] || '').trim(), 
            location: String(r[iLoc]  || '').trim(),
            status:   String(r[iStat] || '').trim(),
            quality:  String(r[iQual] || '').trim(),
          }))
          .filter(r => r.mac && r.mac !== 'None')

        this.trackingTotalRows = parsed.length
        this.trackingPreview   = parsed.slice(0, 10)   // show first 10 as preview
      } catch (e) {
        alert('Failed to preview file: ' + e.message)
        console.error(e)
      }
    },

    async importTrackingSheet() {
      if (!this.trackingFile) return
      this.trackingImporting = true
      this.trackingResult = null
      try {
        const formData = new FormData()
        formData.append('file', this.trackingFile)

        const res = await fetch('/api/import/excel', {
          method: 'POST',
          body: formData
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.detail || 'Import failed')

        this.trackingResult = data
        // Refresh all stores so device list, companies, batches update
        await Promise.all([
          this.loadDevices(),
          this.$store.dispatch('inventory/loadCompanies'),
          this.$store.dispatch('inventory/loadBatches')
        ])
      } catch (e) {
        this.trackingResult = { success: false, error: e.message }
      } finally {
        this.trackingImporting = false
      }
    },

    clearTracking() {
      this.trackingFile    = null
      this.trackingPreview = []
      this.trackingTotalRows = 0
      this.trackingResult  = null
    },

    // ─────────────────────────────────────────
    // MODE B: Bulk MAC import (unchanged logic)
    // ─────────────────────────────────────────
    onFileChange(event) {
      const file = event.target.files[0] || this.file
      if (!file) return
      this.parseFile(file)
    },

    async parseFile(file) {
      this.rows = []
      try {
        let macs = []
        if (file.name.endsWith('.csv')) {
          const text = await file.text()
          macs = this.extractMacs(text.split('\n').flatMap(line => line.split(',')))
        } else {
          const data = await file.arrayBuffer()
          const workbook = XLSX.read(data, { type: 'array' })
          const sheet = workbook.Sheets[workbook.SheetNames[0]]
          const json = XLSX.utils.sheet_to_json(sheet, { header: 1 })
          const values = json.flatMap(row => row.map(cell => String(cell).trim()))
          macs = this.extractMacs(values)
        }
        const unique = [...new Set(macs)]
        this.rows = unique.map(mac => ({
          mac,
          exists: this.devices.some(d => d.mac === mac)
        }))
      } catch (e) {
        alert('Failed to parse file.')
        console.error(e)
      }
    },

    extractMacs(values) {
      const macPattern = /([0-9A-Fa-f]{12,})/g
      const macs = []
      values.forEach(value => {
        const matches = value.match(macPattern)
        if (matches) macs.push(...matches.map(m => m.toUpperCase()))
      })
      return macs
    },

    async importDevices() {
      if (this.rows.length === 0) return
      let finalLocation = this.bulkLocation
      if (finalLocation === '__new__') {
        const name = this.newCompanyName.trim()
        if (!name) { alert('Please enter a company name.'); return }
        await this.addCompany(name)
        finalLocation = name
      }
      let finalBatch = this.bulkBatch
      if (finalBatch === '__new__') {
        const { number, characteristics, deliveryDate } = this.newBatch
        if (!number || !characteristics || !deliveryDate) { alert('Please fill in all batch fields.'); return }
        await this.addBatch({ number, quantity: 0, characteristics, deliveryDate, history: '' })
        finalBatch = `Batch ${number} (${deliveryDate})`
      }
      this.importing = true
      let count = 0
      for (const row of this.rows) {
        if (row.exists) continue
        try {
          await this.addDevice({
            mac: row.mac,
            currentLocation: finalLocation || 'InTech stock',
            currentStatus: this.bulkStatus,
            batch: finalBatch || 'Unknown',
            qualityIssues: '',
            notes: this.bulkNotes
          })
          count++
        } catch (e) {
          console.error('Failed to import ' + row.mac, e)
        }
      }
      this.importing = false
      alert(`${count} device(s) imported successfully.`)
      this.clearAll()
    },

    clearAll() {
      this.file = null
      this.rows = []
      this.bulkLocation = null
      this.bulkBatch = null
      this.bulkNotes = ''
      this.newCompanyName = ''
      this.newBatch = { number: '', characteristics: '', deliveryDate: '' }
    }
  }
}
</script>